/*
 * Assign flow client state — V1.9.1 child / guardian / teen records.
 *
 * The V1.9.1 design's richer route tree (children, guardian modes, teen
 * view-only access) is NEW presentation + client state layered over the
 * existing single-email allocation API. Routes that end in an email invite
 * (someone-else-adult, guestparent parent-email) submit through the REAL
 * my-api allocation call; the semantics the current API cannot express live
 * here, in a clearly-seamed client store, and are NEVER presented as server
 * writes.
 *
 * // TODO WIRE: child-record persistence — POST/GET a real my-api child
 * //   contract (two pools via `guest`, per PRODUCT-RULES §Children) instead
 * //   of this localStorage store. Shape mirrors the handoff §State table:
 * //   `children: [{ key, guest }]` + `child_{key}` records.
 * // TODO WIRE: seat assignment records for modes 'child' | 'guestchild' and
 * //   the teen my-route (minor + guardian) — my-api holder allocation cannot
 * //   express a child holder / guardian waiver routing / teen view-only
 * //   login yet. Records are stamped by seat key
 * //   ({orderRef}|{ticketIndex}.{seatIndex}) and merged over the my-api seat
 * //   state for display only.
 * // TODO WIRE: teen view-only login issuance — recording `teenEmail` here
 * //   sends NOTHING; the done copy claims a sent login only when the server
 * //   seam reports it sent one (teenLoginEmailSent flag, mirroring the
 * //   inviteEmailSent honesty pattern).
 */

// ---------------------------------------------------------------------------
// Shapes (handoff README §State)
// ---------------------------------------------------------------------------

/** One child record. `guest: false` = the purchaser's OWN child; `guest: true`
 *  = another parent's child the purchaser signs for under a stated permission.
 *  TWO POOLS, NEVER MERGED — the legal basis for signing differs. */
export interface ChildRecord {
  key: string
  guest: boolean
  first: string
  last: string
  dobD: string
  dobM: string
  dobY: string
  gender: string
}

export type LocalAssignMode = 'child' | 'guestchild' | 'guestparent' | 'teen'

/** Full child PROFILE record — the handoff §State `child_{key}` shape (same
 *  field map the profile flow edits for the account holder). Identity facts
 *  (name / DOB / gender) are mirrored onto the ChildRecord row on save so the
 *  Profile list and the assign picker read one set of facts.
 *  // TODO WIRE: my-api has no child-profile contract yet — these records are
 *  //   the purchaser's own typed input, persisted client-side ONLY and never
 *  //   presented as server writes (same seam class as ChildRecord above). */
export type ChildProfileDraft = import('../wizard/wizardModel').WizardDraft

/** A locally-recorded seat assignment for the modes the allocation API cannot
 *  express yet (see the TODO WIRE seam above). */
export interface LocalAssignment {
  seatKey: string
  mode: LocalAssignMode
  /** Holder display name (child/teen name; '' when unknown on guestparent). */
  name: string
  childKey: string
  tType: string
  evName: string
  parentEmail: string
  parentName: string
  minor: boolean
  teenLogin: boolean
  teenEmail: string
  /** The seat's my-api ticket ROW id — stamped at assign time since the
   *  child-identity slice (BOARDROOM-2026-09-02-150714 item 2) so surfaces
   *  keyed by ticketId (Get-ready per-holding cards) can find the record
   *  without re-deriving the seat-index mapping. OPTIONAL: records written
   *  before the field existed lack it until backfilled (Orders stamps it the
   *  moment the seat's wire ticketId is in hand; Get-ready resolves via the
   *  order's allocations read). */
  ticketId?: string
}

interface StoreShape {
  children: ChildRecord[]
  assigned: Record<string, LocalAssignment>
  /** `child_{key}` profile records keyed by ChildRecord.key (handoff §State). */
  childProfiles: Record<string, ChildProfileDraft>
}

// ---------------------------------------------------------------------------
// Store — module-level + localStorage persistence, useSyncExternalStore-ready
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'tru.assignStore.v1'

function readStored(): StoreShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { children: [], assigned: {}, childProfiles: {} }
    const parsed = JSON.parse(raw) as Partial<StoreShape>
    return {
      children: Array.isArray(parsed.children) ? parsed.children : [],
      assigned: parsed.assigned && typeof parsed.assigned === 'object' ? parsed.assigned : {},
      childProfiles:
        parsed.childProfiles && typeof parsed.childProfiles === 'object' ? parsed.childProfiles : {},
    }
  } catch {
    return { children: [], assigned: {}, childProfiles: {} }
  }
}

let state: StoreShape = readStored()
const listeners = new Set<() => void>()

function persist({ notify = true }: { notify?: boolean } = {}) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Persistence unavailable (private mode etc.) — in-session state still applies.
  }
  // notify=false = a SILENT write (the ticketId backfill stamp only): nothing
  // rendered depends on the stamp, and notifying would restart the live
  // Get-ready read mid-flight (PR#124 review P1-1 — the flaky early-abort
  // interference) for a value the pass in flight already resolved.
  if (notify) listeners.forEach((l) => l())
}

export function subscribeAssignStore(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

export const getAssignStoreSnapshot = (): StoreShape => state

/** All children in ONE pool — callers must filter by `guest` (the picker only
 *  ever offers the pool matching the chosen route). */
export function listChildren(): ChildRecord[] {
  return state.children
}

export function addChild(child: Omit<ChildRecord, 'key'>): ChildRecord {
  const rec: ChildRecord = { ...child, key: `k${Date.now()}${Math.floor(Math.random() * 1e4)}` }
  state = { ...state, children: [...state.children, rec] }
  persist()
  return rec
}

export function getLocalAssignment(seatKey: string): LocalAssignment | null {
  return state.assigned[seatKey] ?? null
}

export function recordLocalAssignment(a: LocalAssignment): void {
  state = { ...state, assigned: { ...state.assigned, [a.seatKey]: a } }
  persist()
}

/** Child-IDENTITY modes: the seat's subject is a recorded child/teen the
 *  account holder signs for — the ticket is identified by the CHILD's name on
 *  every surface (Duncan ruling, BOARDROOM-2026-09-02-150714 item 2).
 *  'guestparent' is deliberately EXCLUDED: that seat's server holder is the
 *  child's own parent (invitee) and renders through the waiting overlay. */
export function isChildIdentityMode(mode: LocalAssignMode): boolean {
  return mode === 'child' || mode === 'guestchild' || mode === 'teen'
}

/** The record's subject display name — the FRESH ChildRecord facts (kept
 *  current by profile saves) win over the assignment-time snapshot `name`, so
 *  a renamed child never renders a stale ticket label. '' when neither knows
 *  (honest omission downstream — never a fabricated or guardian name). */
export function localAssignmentDisplayName(a: LocalAssignment): string {
  const rec = a.childKey ? state.children.find((c) => c.key === a.childKey) : undefined
  const fresh = rec ? `${rec.first} ${rec.last}`.trim() : ''
  return fresh || a.name.trim()
}

/** The local record for a seat by its my-api ticket ROW id (null when none /
 *  the record predates the ticketId stamp and is not yet backfilled). */
export function localAssignmentForTicket(ticketId: string): LocalAssignment | null {
  if (!ticketId) return null
  return Object.values(state.assigned).find((a) => a.ticketId === ticketId) ?? null
}

/** DECLARED-PERMISSION seat gate for the wizard's SELF eligibility-advisory
 *  filter (dispatch BOARDROOM-2026-09-07-103021 — the guardianRecordApplies
 *  twin in data/guardianClaim.ts): a seat locally recorded in a CHILD-identity
 *  mode (child/guestchild/teen) is a minor's seat the account holder manages —
 *  its ticket contract applies to the CHILD, so the PARENT's own profile must
 *  never be advisory-warned against it (the guardian live-walk defect class).
 *  Same stale-record guard: an EXPLICIT wire kind='self' outranks a lingering
 *  record; absent kind keeps the record applying. Display-filter input only —
 *  no gating behaviour is added anywhere. */
export function declaredChildRecordApplies(
  ticketId: string,
  kind?: import('../../data/myApi').HolderKind,
): boolean {
  if (!ticketId || kind === 'self') return false
  const rec = Object.values(state.assigned).find((a) => a.ticketId === ticketId)
  return !!rec && isChildIdentityMode(rec.mode)
}

/** BACKFILL / REVALIDATION stamp: (re)stamp a record's my-api ticket row id
 *  once a surface has both in hand (Orders knows every seat's wire ticketId;
 *  Get-ready resolves it via the order allocations read). OVERWRITES a stale
 *  stamp — the freeze is never trusted over a fresh wire read (PR#124 review
 *  P2-2). SILENT (no subscriber notification): nothing rendered depends on
 *  the stamp, and notifying restarted the live read mid-flight (review
 *  P1-1). No-op when the record is absent or the stamp already matches. */
export function stampLocalAssignmentTicketId(seatKey: string, ticketId: string): void {
  const rec = state.assigned[seatKey]
  if (!rec || !ticketId || rec.ticketId === ticketId) return
  state = { ...state, assigned: { ...state.assigned, [seatKey]: { ...rec, ticketId } } }
  persist({ notify: false })
}

export function clearLocalAssignment(seatKey: string): void {
  if (!(seatKey in state.assigned)) return
  const next = { ...state.assigned }
  delete next[seatKey]
  state = { ...state, assigned: next }
  persist()
}

/** Child profile record (`child_{key}`) — null until first saved. */
export function getChildProfile(key: string): ChildProfileDraft | null {
  return state.childProfiles[key] ?? null
}

/** Save a child's profile draft AND mirror the identity facts (name / DOB /
 *  gender) onto the ChildRecord row, so the Profile list, the assign picker
 *  and the flow all read ONE set of facts. `guest` is NEVER touched here —
 *  a record can never migrate between the two pools by editing a profile. */
export function saveChildProfile(key: string, draft: ChildProfileDraft): void {
  state = {
    ...state,
    childProfiles: { ...state.childProfiles, [key]: draft },
    children: state.children.map((c) =>
      c.key === key
        ? {
            ...c,
            first: draft.first, last: draft.last,
            dobD: draft.dobD, dobM: draft.dobM, dobY: draft.dobY,
            gender: draft.gender,
          }
        : c,
    ),
  }
  persist()
}

/** TEST-ONLY reset. */
export function resetAssignStoreForTests(): void {
  state = { children: [], assigned: {}, childProfiles: {} }
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* noop */ }
  listeners.forEach((l) => l())
}
